import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useSalesTrainingStore } from '../store/sales-training-store';
import {
  SalesProfileUpdate,
  ObjectionPair,
  TONE_OPTIONS,
  CLOSING_STYLE_OPTIONS,
} from '../types/sales-training.types';
import { Bot, Save, Plus, X, Loader2 } from 'lucide-react';

export function SalesProfileEditor() {
  const { profile, isLoading, fetchProfile, updateProfile } = useSalesTrainingStore();

  const [formData, setFormData] = useState<SalesProfileUpdate>({
    agentName: 'Asistente de Ventas',
    tone: 'profesional',
    closingStyle: 'consultivo',
    greetingTemplate: '',
    objectionHandling: [],
    negotiationRules: [],
    keyPhrases: [],
    forbiddenPhrases: [],
    maxDiscountWithoutApproval: 10,
    upsellEnabled: true,
    crossSellEnabled: true,
  });

  const [newKeyPhrase, setNewKeyPhrase] = useState('');
  const [newForbiddenPhrase, setNewForbiddenPhrase] = useState('');
  const [newRule, setNewRule] = useState('');
  const [newObjection, setNewObjection] = useState<ObjectionPair>({ objection: '', response: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (profile) {
      setFormData({
        agentName: profile.agentName,
        tone: profile.tone,
        closingStyle: profile.closingStyle,
        greetingTemplate: profile.greetingTemplate || '',
        objectionHandling: profile.objectionHandling || [],
        negotiationRules: profile.negotiationRules || [],
        keyPhrases: profile.keyPhrases || [],
        forbiddenPhrases: profile.forbiddenPhrases || [],
        maxDiscountWithoutApproval: profile.maxDiscountWithoutApproval,
        upsellEnabled: profile.upsellEnabled,
        crossSellEnabled: profile.crossSellEnabled,
      });
    }
  }, [profile]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateProfile(formData);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // error handled by store
    } finally {
      setSaving(false);
    }
  }, [formData, updateProfile]);

  const addKeyPhrase = () => {
    if (newKeyPhrase.trim()) {
      setFormData((prev) => ({
        ...prev,
        keyPhrases: [...(prev.keyPhrases || []), newKeyPhrase.trim()],
      }));
      setNewKeyPhrase('');
    }
  };

  const removeKeyPhrase = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      keyPhrases: (prev.keyPhrases || []).filter((_, i) => i !== index),
    }));
  };

  const addForbiddenPhrase = () => {
    if (newForbiddenPhrase.trim()) {
      setFormData((prev) => ({
        ...prev,
        forbiddenPhrases: [...(prev.forbiddenPhrases || []), newForbiddenPhrase.trim()],
      }));
      setNewForbiddenPhrase('');
    }
  };

  const removeForbiddenPhrase = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      forbiddenPhrases: (prev.forbiddenPhrases || []).filter((_, i) => i !== index),
    }));
  };

  const addRule = () => {
    if (newRule.trim()) {
      setFormData((prev) => ({
        ...prev,
        negotiationRules: [...(prev.negotiationRules || []), newRule.trim()],
      }));
      setNewRule('');
    }
  };

  const removeRule = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      negotiationRules: (prev.negotiationRules || []).filter((_, i) => i !== index),
    }));
  };

  const addObjection = () => {
    if (newObjection.objection.trim() && newObjection.response.trim()) {
      setFormData((prev) => ({
        ...prev,
        objectionHandling: [...(prev.objectionHandling || []), { ...newObjection }],
      }));
      setNewObjection({ objection: '', response: '' });
    }
  };

  const removeObjection = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      objectionHandling: (prev.objectionHandling || []).filter((_, i) => i !== index),
    }));
  };

  if (isLoading && !profile) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Identidad del Agente */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Identidad del Agente</CardTitle>
          </div>
          <CardDescription>
            Define la personalidad y estilo de comunicación de tu asistente de ventas IA
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="agentName">Nombre del agente</Label>
              <Input
                id="agentName"
                value={formData.agentName || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, agentName: e.target.value }))}
                placeholder="Ej: Ana, Asistente Datec"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tone">Tono</Label>
              <SearchableSelect
                value={formData.tone || 'profesional'}
                onValueChange={(val) => setFormData((prev) => ({ ...prev, tone: val as any }))}
                options={TONE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                placeholder="Seleccionar tono..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closingStyle">Estilo de cierre</Label>
              <SearchableSelect
                value={formData.closingStyle || 'consultivo'}
                onValueChange={(val) =>
                  setFormData((prev) => ({ ...prev, closingStyle: val as any }))
                }
                options={CLOSING_STYLE_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
                placeholder="Seleccionar estilo..."
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="greetingTemplate">Saludo modelo</Label>
            <textarea
              id="greetingTemplate"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={formData.greetingTemplate || ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, greetingTemplate: e.target.value }))
              }
              placeholder="Ej: Hola, soy {agentName} de Datec. ¿En que puedo ayudarte hoy?"
            />
          </div>
        </CardContent>
      </Card>

      {/* Frases Clave y Prohibidas */}
      <Card>
        <CardHeader>
          <CardTitle>Frases Clave y Prohibidas</CardTitle>
          <CardDescription>
            Frases que el agente debe usar o evitar en sus respuestas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Frases Clave */}
          <div className="space-y-3">
            <Label>Frases clave (el agente las usara naturalmente)</Label>
            <div className="flex flex-wrap gap-2">
              {(formData.keyPhrases || []).map((phrase, i) => (
                <Badge key={i} variant="secondary" className="gap-1 pr-1">
                  {phrase}
                  <button
                    onClick={() => removeKeyPhrase(i)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newKeyPhrase}
                onChange={(e) => setNewKeyPhrase(e.target.value)}
                placeholder="Nueva frase clave..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyPhrase())}
              />
              <Button variant="outline" size="sm" onClick={addKeyPhrase}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Frases Prohibidas */}
          <div className="space-y-3">
            <Label>Frases prohibidas (el agente NUNCA las usara)</Label>
            <div className="flex flex-wrap gap-2">
              {(formData.forbiddenPhrases || []).map((phrase, i) => (
                <Badge key={i} variant="destructive" className="gap-1 pr-1">
                  {phrase}
                  <button
                    onClick={() => removeForbiddenPhrase(i)}
                    className="ml-1 hover:text-white"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newForbiddenPhrase}
                onChange={(e) => setNewForbiddenPhrase(e.target.value)}
                placeholder="Nueva frase prohibida..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addForbiddenPhrase())}
              />
              <Button variant="outline" size="sm" onClick={addForbiddenPhrase}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Manejo de Objeciones */}
      <Card>
        <CardHeader>
          <CardTitle>Manejo de Objeciones</CardTitle>
          <CardDescription>
            Define como debe responder el agente ante objeciones comunes de clientes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(formData.objectionHandling || []).map((pair, i) => (
            <div key={i} className="flex gap-3 items-start p-3 rounded-md bg-muted/50">
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Objeción:</p>
                <p className="text-sm">{pair.objection}</p>
                <p className="text-sm font-medium text-muted-foreground mt-2">Respuesta:</p>
                <p className="text-sm">{pair.response}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeObjection(i)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="space-y-3 p-3 rounded-md border border-dashed">
            <textarea
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={newObjection.objection}
              onChange={(e) => setNewObjection((prev) => ({ ...prev, objection: e.target.value }))}
              placeholder='Objeción del cliente, ej: "El precio es muy alto"'
            />
            <textarea
              className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={newObjection.response}
              onChange={(e) => setNewObjection((prev) => ({ ...prev, response: e.target.value }))}
              placeholder="Como debe responder el agente..."
            />
            <Button variant="outline" size="sm" onClick={addObjection}>
              <Plus className="h-4 w-4 mr-1" /> Agregar objeción
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reglas de Negociacion */}
      <Card>
        <CardHeader>
          <CardTitle>Reglas de Negociación</CardTitle>
          <CardDescription>
            Lineamientos que el agente debe seguir durante negociaciones de precio
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            {(formData.negotiationRules || []).map((rule, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                <span className="text-sm font-medium text-muted-foreground w-6">{i + 1}.</span>
                <span className="text-sm flex-1">{rule}</span>
                <Button variant="ghost" size="sm" onClick={() => removeRule(i)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              placeholder="Nueva regla de negociación..."
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRule())}
            />
            <Button variant="outline" size="sm" onClick={addRule}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reglas de Pricing */}
      <Card>
        <CardHeader>
          <CardTitle>Reglas de Pricing</CardTitle>
          <CardDescription>
            Configuración de limites y comportamientos de pricing del agente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxDiscount">Descuento máximo sin aprobación (%)</Label>
              <Input
                id="maxDiscount"
                type="number"
                min={0}
                max={100}
                value={formData.maxDiscountWithoutApproval || 0}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    maxDiscountWithoutApproval: Number(e.target.value),
                  }))
                }
              />
            </div>
            <div className="space-y-3 pt-6">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="upsell"
                  checked={formData.upsellEnabled ?? true}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, upsellEnabled: checked === true }))
                  }
                />
                <Label htmlFor="upsell" className="cursor-pointer">
                  Upselling habilitado
                </Label>
              </div>
            </div>
            <div className="space-y-3 pt-6">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="crossSell"
                  checked={formData.crossSellEnabled ?? true}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, crossSellEnabled: checked === true }))
                  }
                />
                <Label htmlFor="crossSell" className="cursor-pointer">
                  Cross-selling habilitado
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Boton Guardar */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {saved ? 'Guardado' : 'Guardar Perfil'}
        </Button>
      </div>
    </div>
  );
}

export default SalesProfileEditor;
